import { ReactElement, useEffect } from 'react';
import { Grid, SelectChangeEvent, useEventCallback } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { useFormikContext } from 'formik';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import ListRegister from 'civil-status/components/shared/list-register';
import { ResearchNotifications } from 'notifications-from-civil-status/view-models/notifications-list';
import ListAnscValoriDominio from 'civil-status/components/shared/list-ansc-valori-dominio';
import { DomainType } from 'civil-status/constants/domain';
import { CodeScope } from 'civil-status/constants/code-scope';
import OperatorList from 'civil-status/components/shared/operator-list';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import ListTypeComunication from 'shell/components/shared/list-type-comunication';

interface FormFilterProps {
  idCat?: string;
  scope?: string;
}

const FormFilter = ({ idCat, scope }: FormFilterProps): ReactElement => {
  const { userProfile } = useUserProfile();
  const formContext = useFormikContext<ResearchNotifications>();

  const onChangeOperator = useEventCallback((event: SelectChangeEvent<string>) => {
    formContext.setFieldValue('codOperator', event.target.value);
  });

  const onChangeRegister = useEventCallback((event: SelectChangeEvent<string>) => {
    formContext.setFieldValue('typeEvent', event.target.value);
  });

  useEffect(() => {
    if (idCat) {
      formContext.setFieldValue('typeEvent', idCat);
    }
  }, [idCat]);

  useEffect(() => {
    if (scope) {
      formContext.setFieldValue('scope', scope);
    }
  }, [scope]);

  useEffect(() => {
    const currentCodeOperator = userProfile.datiLogin?.key?.userName || '';

    if (currentCodeOperator) {
      formContext.setFieldValue('codOperator', currentCodeOperator);
    }
  }, [userProfile]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <ListRegister
          value={formContext.values.typeEvent || ''}
          error={Boolean(formContext.errors.typeEvent) && formContext.touched.typeEvent}
          onChange={onChangeRegister}
          disabled={Boolean(idCat)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <DatePicker
          label="Data inizio"
          aria-label="Data inizio"
          value={formContext.values.dateStart || null}
          onChange={(value) => {
            if (formContext.values.dateEnd) {
              formContext.setFieldValue('dateStart', value);
              return;
            }

            formContext.setValues({
              ...formContext.values,
              dateStart: value,
              dateEnd: value,
            });
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateStart"
              fullWidth
              error={formContext.touched?.dateStart && Boolean(formContext.errors?.dateStart)}
              helperText={formContext.errors?.dateStart}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <DatePicker
          label="Data fine"
          aria-label="Data fine"
          value={formContext.values.dateEnd || null}
          onChange={(value) => formContext.setFieldValue('dateEnd', value)}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateEnd"
              fullWidth
              error={formContext.touched?.dateEnd && Boolean(formContext.errors?.dateEnd)}
              helperText={formContext.errors?.dateEnd}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <ListTypeComunication
          label="Codice comunicazione"
          value={formContext.values.code || ''}
          onChange={(el) => formContext.setFieldValue('code', el)}
          error={formContext.touched.code && Boolean(formContext.errors.code)}
          ambito={CodeScope.COMUNIC}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <ListAnscValoriDominio
          label="Stato aggiornamento"
          value={formContext.values.state || ''}
          onChange={(el) => formContext.setFieldValue('state', el)}
          error={formContext.touched.state && Boolean(formContext.errors.state)}
          idDominio={DomainType.Archive}
          idValoreAnsc={CodeScope.COMUNIC}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <OperatorList
          onChange={onChangeOperator}
          value={formContext.values.codOperator || ''}
          // codiceStatoEvento={formContext.values.stateAct || undefined}
          // disabled={!formContext.values.stateAct}
        />
      </Grid>

      <Grid item container spacing={2} xs={12} justifyContent="space-between" display="flex">
        <Grid item container spacing={2} xs={12} justifyContent="flex-end" display="flex">
          <Grid item xs={12} md={2}>
            <PmiButton
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() =>
                formContext.setValues({
                  code: '',
                  dateEnd: null,
                  dateStart: null,
                  state: '',
                  typeEvent: idCat || undefined,
                })
              }
            >
              Pulisci
            </PmiButton>
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiButton variant="contained" color="secondary" fullWidth type="submit">
              Cerca
            </PmiButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

FormFilter.displayName =
  'Research notifications - Filters component - AccordionFilterNotifications - Form Filter component';

export default FormFilter;
